import { useMemo, useState } from "react";
import { CaPageHeader, usePath, friendlyTime } from "@/company-admin/shared";
import { Button, Field, Input, Select, Modal, StatusPill } from "@/admin/ui";
import CredentialModal from "@/admin/CredentialModal";
import DataTable from "@/admin/DataTable";
import { caApi, formatApiError } from "@/company-admin/api";

const STATUSES = ["active", "invited", "suspended", "removed"];
const EMPLOYMENT = ["full_time", "part_time", "contract", "intern"];
const ROLES = ["ceo", "sales", "finance", "hr", "operations", "marketing", "customer", "procurement", "legal", "strategy", "meeting"];

const empty = {
    first_name: "", last_name: "", email: "", phone: "",
    role: "sales", department: "", manager_id: "",
    joining_date: "", employment_type: "full_time",
    location: "", employee_id: "", status: "active",
};

export default function Employees() {
    const { data, loading, refresh, error } = usePath("/company-admin/employees");
    const departments = usePath("/company-admin/departments");
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [search, setSearch] = useState("");
    const [fRole, setFRole] = useState("");
    const [fDept, setFDept] = useState("");
    const [fStatus, setFStatus] = useState("");
    const [cred, setCred] = useState(null);
    const [pwMode, setPwMode] = useState("auto");
    const [pwValue, setPwValue] = useState("");

    const rows = useMemo(() => {
        const list = data || [];
        const q = search.toLowerCase().trim();
        return list.filter((u) => {
            if (fRole && u.role !== fRole) return false;
            if (fDept && u.department !== fDept) return false;
            if (fStatus && u.status !== fStatus) return false;
            if (!q) return true;
            return (
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                (u.employee_id || "").toLowerCase().includes(q)
            );
        });
    }, [data, search, fRole, fDept, fStatus]);

    function openCreate() { setEditing(null); setForm(empty); setPwMode("auto"); setPwValue(""); setErr(""); setOpen(true); }
    function openEdit(u) {
        setEditing(u);
        const parts = (u.name || "").split(" ");
        setForm({
            ...empty, ...u,
            first_name: u.first_name || parts[0] || "",
            last_name: u.last_name || parts.slice(1).join(" ") || "",
        });
        setErr(""); setOpen(true);
    }

    async function save() {
        setBusy(true); setErr("");
        try {
            const payload = { ...form };
            if (editing) {
                await caApi.patch(`/company-admin/employees/${editing.id}`, payload);
            } else {
                const r = await caApi.post("/company-admin/employees", {
                    ...payload,
                    password_mode: pwMode,
                    password: pwMode === "manual" ? pwValue : "",
                });
                setCred({ email: r.data.email, password: r.data.temp_password, roleLabel: r.data.role });
            }
            setOpen(false);
            await refresh();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function toggleStatus(u, next) {
        try {
            await caApi.patch(`/company-admin/employees/${u.id}`, { status: next });
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }
    async function resetPw(u) {
        try {
            const r = await caApi.post(`/company-admin/employees/${u.id}/reset-password`);
            setCred({ email: u.email, password: r.data.temp_password, roleLabel: u.role });
        } catch (e) {
            alert(formatApiError(e));
        }
    }
    async function del(u) {
        if (!confirm(`Delete employee ${u.name}? This cannot be undone.`)) return;
        try {
            await caApi.delete(`/company-admin/employees/${u.id}`);
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }
    function exportCsv() {
        const cols = ["name", "email", "phone", "role", "department", "status", "last_login", "joining_date"];
        const csv = [cols.join(",")]
            .concat((data || []).map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(",")))
            .join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "employees.csv";
        a.click();
    }

    const cols = [
        {
            key: "name",
            label: "Employee",
            render: (r) => (
                <div className="flex items-center gap-2.5">
                    <span className="h-8 w-8 rounded-full bg-black text-white text-[11px] font-semibold inline-flex items-center justify-center">
                        {(r.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div>
                        <div className="font-semibold text-black">{r.name}</div>
                        <div className="text-[12px] text-neutral-500">{r.email}</div>
                    </div>
                </div>
            ),
        },
        { key: "department", label: "Department", render: (r) => r.department || "—" },
        {
            key: "role", label: "Role",
            render: (r) => <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2.5 py-1">{r.role}</span>,
        },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
        { key: "last_login", label: "Last Login", render: (r) => <span className="text-[12px] text-neutral-500">{r.last_login ? friendlyTime(r.last_login) : "—"}</span> },
        {
            key: "actions", label: "",
            render: (r) => (
                <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`emp-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="subtle" onClick={() => resetPw(r)} data-testid={`emp-reset-${r.id}`}>Reset PW</Button>
                    {r.status !== "suspended" ? (
                        <Button size="sm" variant="subtle" onClick={() => toggleStatus(r, "suspended")} data-testid={`emp-suspend-${r.id}`}>Deactivate</Button>
                    ) : (
                        <Button size="sm" variant="subtle" onClick={() => toggleStatus(r, "active")} data-testid={`emp-activate-${r.id}`}>Reactivate</Button>
                    )}
                    <Button size="sm" variant="danger" onClick={() => del(r)} data-testid={`emp-delete-${r.id}`}>Delete</Button>
                </div>
            ),
            tdClass: "text-right",
        },
    ];

    return (
        <div data-testid="ca-employees-page">
            <CaPageHeader
                title="Employees"
                subtitle="Add, invite, and manage every person in your company workspace."
                action={
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" onClick={exportCsv} data-testid="emp-export">Export CSV</Button>
                        <Button size="lg" onClick={openCreate} data-testid="emp-add-btn">+ Add Employee</Button>
                    </div>
                }
            />

            <div className="glass rounded-3xl p-4 mb-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="emp-search" />
                <Select value={fRole} onChange={(e) => setFRole(e.target.value)}>
                    <option value="">All roles</option>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
                <Select value={fDept} onChange={(e) => setFDept(e.target.value)}>
                    <option value="">All departments</option>
                    {(departments.data || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                </Select>
                <Select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
                    <option value="">All statuses</option>
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                </Select>
            </div>

            {error && <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-5">{error}</div>}

            <DataTable
                cols={cols}
                rows={rows}
                rowKey={(r) => r.id}
                loading={loading}
                empty={{ title: "No employees yet", hint: "Add your first employee or send an invitation." }}
            />

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={editing ? "Edit Employee" : "Add Employee"}
                size="lg"
                testid="emp-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} disabled={busy} data-testid="emp-save-btn">
                            {busy ? "Saving…" : editing ? "Save" : "Save Employee"}
                        </Button>
                    </>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="First Name"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="emp-first-name" /></Field>
                    <Field label="Last Name"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="emp-last-name" /></Field>
                    <Field label="Email"><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" /></Field>
                    <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                    <Field label="Department">
                        <Select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="emp-department">
                            <option value="">— None —</option>
                            {(departments.data || []).map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </Select>
                    </Field>
                    <Field label="Role">
                        <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="emp-role">
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Select>
                    </Field>
                    <Field label="Reporting Manager">
                        <Select value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })}>
                            <option value="">— None —</option>
                            {(data || []).filter((u) => u.id !== editing?.id).map((u) => (
                                <option key={u.id} value={u.id}>{`${u.name} (${u.role})`}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Joining Date"><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} /></Field>
                    <Field label="Employment Type">
                        <Select value={form.employment_type} onChange={(e) => setForm({ ...form, employment_type: e.target.value })}>
                            {EMPLOYMENT.map((e2) => <option key={e2}>{e2}</option>)}
                        </Select>
                    </Field>
                    <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
                    <Field label="Employee ID"><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} /></Field>
                    <Field label="Status">
                        <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </Select>
                    </Field>
                    {!editing && (
                        <div className="sm:col-span-2 rounded-2xl border border-black/10 bg-neutral-50/60 p-4">
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500 mb-2">
                                Login Password
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                    <input
                                        type="radio"
                                        name="emp-pw-mode"
                                        checked={pwMode === "auto"}
                                        onChange={() => setPwMode("auto")}
                                        data-testid="emp-pw-mode-auto"
                                    />
                                    Auto-generate a strong password
                                </label>
                                <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                    <input
                                        type="radio"
                                        name="emp-pw-mode"
                                        checked={pwMode === "manual"}
                                        onChange={() => setPwMode("manual")}
                                        data-testid="emp-pw-mode-manual"
                                    />
                                    Set password manually
                                </label>
                            </div>
                            {pwMode === "manual" && (
                                <div className="mt-3">
                                    <Input
                                        type="text"
                                        placeholder="Min 8 chars, incl. a letter and a number"
                                        value={pwValue}
                                        onChange={(e) => setPwValue(e.target.value)}
                                        data-testid="emp-pw-value"
                                    />
                                </div>
                            )}
                            <div className="mt-2 text-[12px] text-neutral-500">
                                The password is shown once after creation. The employee must change it on first login.
                            </div>
                        </div>
                    )}
                    {err && <div className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">{err}</div>}
                </div>
            </Modal>

            <CredentialModal
                open={!!cred}
                onClose={() => setCred(null)}
                email={cred?.email}
                password={cred?.password}
                roleLabel={cred?.roleLabel}
            />
        </div>
    );
}
